// Loading.tsx - 简化版本，移除重复的认证逻辑

import { useEffect, useState } from 'react'
import { logger } from '../config'

interface LoadingProps {
  message?: string
  showProgress?: boolean
  onLoadingComplete?: (result: LoadingResult) => void
  minDuration?: number
  stepDelays?: {
    checkAuth?: number
    login?: number
    userData?: number
    gameData?: number
  }
}

interface LoadingResult {
  success: boolean
  userInfo?: any
  currentPeriod?: any
  balance?: number
  error?: string
}

export default function Loading({
                                  message = '正在載入...',
                                  showProgress = true,
                                  onLoadingComplete,
                                  minDuration = 4000,
                                  stepDelays = {
                                    checkAuth: 800,
                                    login: 1200,
                                    userData: 1000,
                                    gameData: 1000
                                  }
                                }: LoadingProps) {
  const [progress, setProgress] = useState(0)
  const [dots, setDots] = useState('')
  const [currentMessage, setCurrentMessage] = useState(message)

  // Loading流程状态
  const [loadingSteps] = useState([
    { name: '初始化系統', weight: 25 },
    { name: '載入配置', weight: 25 },
    { name: '準備界面', weight: 25 },
    { name: '完成載入', weight: 25 }
  ])

  // 更新进度和消息
  const updateProgress = (step: number, stepProgress: number = 100) => {
    const completedWeight = loadingSteps.slice(0, step).reduce((sum, s) => sum + s.weight, 0)
    const currentStepWeight = loadingSteps[step]?.weight || 0
    const currentStepProgress = (currentStepWeight * stepProgress) / 100
    const totalProgress = completedWeight + currentStepProgress

    setProgress(Math.min(totalProgress, 95))
    if (loadingSteps[step]) {
      setCurrentMessage(loadingSteps[step].name + (stepProgress < 100 ? '中...' : '完成'))
    }
  }

  // 添加延迟函数
  const delay = (ms: number) => new Promise(resolve => setTimeout(resolve, ms))

  // 模拟渐进式进度更新
  const smoothProgressUpdate = async (step: number, duration: number = 1000) => {
    const startProgress = loadingSteps.slice(0, step).reduce((sum, s) => sum + s.weight, 0)
    const endProgress = startProgress + loadingSteps[step].weight
    const steps = 10
    const stepDuration = duration / steps

    for (let i = 0; i <= steps; i++) {
      const currentProgress = startProgress + (loadingSteps[step].weight * i / steps)
      setProgress(Math.min(currentProgress, 95))
      await delay(stepDuration)
    }
  }

  // 🔧 简化的Loading序列 - 不再执行认证逻辑
  const performLoadingSequence = async (): Promise<LoadingResult> => {
    try {
      // 步骤1: 初始化系统
      setCurrentMessage('初始化系統中...')
      await smoothProgressUpdate(0, stepDelays?.checkAuth || 800)

      // 步骤2: 载入配置
      setCurrentMessage('載入配置中...')
      await smoothProgressUpdate(1, stepDelays?.login || 1200)

      // 步骤3: 准备界面
      setCurrentMessage('準備界面中...')
      await smoothProgressUpdate(2, stepDelays?.userData || 1000)

      // 步骤4: 完成加载
      setCurrentMessage('完成載入中...')
      await smoothProgressUpdate(3, stepDelays?.gameData || 1000)

      // 最后设置为100%
      setCurrentMessage('載入完成！')
      setProgress(100)
      await delay(500)

      // 让AuthContext负责处理认证逻辑
      return {
        success: true
      }

    } catch (error) {
      logger.error('Loading流程失敗:', error)
      const errorMessage = error instanceof Error ? error.message : '載入失敗'
      setCurrentMessage(`載入失敗: ${errorMessage}`)

      return {
        success: false,
        error: errorMessage
      }
    }
  }

  useEffect(() => {
    const startTime = Date.now()

    // 启动Loading序列
    performLoadingSequence().then(result => {
      const elapsed = Date.now() - startTime
      const remainingTime = Math.max(0, minDuration - elapsed)

      // 确保Loading至少显示指定的最小时间
      setTimeout(() => {
        if (onLoadingComplete) {
          onLoadingComplete(result)
        }
      }, remainingTime + 800)
    })
  }, [])

  useEffect(() => {
    // 载入点点动画
    const dotTimer = setInterval(() => {
      setDots(prev => {
        if (prev.length >= 3) return ''
        return prev + '.'
      })
    }, 500)
    return () => clearInterval(dotTimer)
  }, [])

  return (
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        background: 'var(--gradient-primary)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 9999,
        color: 'white'
      }}>
        {/* 背景裝飾 */}
        <div style={{
          position: 'absolute',
          top: '10%',
          left: '10%',
          fontSize: '6em',
          opacity: 0.1,
          animation: 'float 3s ease-in-out infinite'
        }}>
          🎲
        </div>
        <div style={{
          position: 'absolute',
          top: '20%',
          right: '15%',
          fontSize: '4em',
          opacity: 0.1,
          animation: 'float 3s ease-in-out infinite 1s'
        }}>
          💰
        </div>
        <div style={{
          position: 'absolute',
          bottom: '15%',
          left: '20%',
          fontSize: '5em',
          opacity: 0.1,
          animation: 'float 3s ease-in-out infinite 2s'
        }}>
          🎯
        </div>

        {/* 主要載入內容 */}
        <div style={{
          textAlign: 'center',
          position: 'relative',
          zIndex: 1
        }}>
          {/* Logo */}
          <div style={{
            fontSize: '4em',
            marginBottom: '30px',
            animation: 'dice-roll 2s linear infinite'
          }}>
            🎲
          </div>

          {/* 應用標題 */}
          <h1 style={{
            fontSize: '2.5em',
            fontWeight: '900',
            marginBottom: '20px',
            textShadow: '0 4px 8px rgba(0, 0, 0, 0.3)',
            background: 'linear-gradient(45deg, #ffd700, #ffed4a)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text'
          }}>
            快三遊戲
          </h1>

          {/* 載入動畫 */}
          <div style={{
            width: '60px',
            height: '60px',
            margin: '30px auto',
            border: '4px solid rgba(255, 255, 255, 0.3)',
            borderTop: '4px solid #ffd700',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite'
          }} />

          {/* 載入訊息 */}
          <p style={{
            fontSize: '1.2em',
            marginBottom: '20px',
            fontWeight: '600',
            minHeight: '30px'
          }}>
            {currentMessage}{dots}
          </p>

          {/* 進度條 */}
          {showProgress && (
              <div style={{
                width: '300px',
                height: '6px',
                background: 'rgba(255, 255, 255, 0.2)',
                borderRadius: '3px',
                margin: '0 auto',
                overflow: 'hidden'
              }}>
                <div style={{
                  width: `${progress}%`,
                  height: '100%',
                  background: 'linear-gradient(90deg, #ffd700, #ffed4a)',
                  borderRadius: '3px',
                  transition: 'width 0.3s ease',
                  boxShadow: '0 0 10px rgba(255, 215, 0, 0.5)'
                }} />
              </div>
          )}

          {showProgress && (
              <div style={{
                marginTop: '10px',
                fontSize: '0.9em',
                opacity: 0.8
              }}>
                {Math.round(progress)}%
              </div>
          )}

          {/* 版權信息 */}
          <div style={{
            position: 'fixed',
            bottom: '20px',
            left: '50%',
            transform: 'translateX(-50%)',
            fontSize: '0.9em',
            opacity: 0.7,
            textAlign: 'center'
          }}>
            正在為您準備最佳遊戲體驗...
          </div>
        </div>

        <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        
        @keyframes float {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-20px); }
        }
        
        @keyframes dice-roll {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
      </div>
  )
}